/* eslint-disable @typescript-eslint/no-unused-vars */
import { Settings, Trash2 } from "lucide-react";
import { usePlatformSettings } from "./usePlatformSettings";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/shadcn/ui/card";
import { Input } from "components/shadcn/ui/input";
import { Switch } from "components/shadcn/ui/switch";
import { Button } from "components/shadcn/ui/button";
import { Label } from "components/shadcn/ui/label";
import { ScrollArea } from "components/shadcn/ui/scrollArea";
import { Alert } from "components/shadcn/ui/alert";
import Loader from "components/common/loader";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";

export default function PlatformSettings() {
  const {
    platformSettingsCopy,
    calendarUrl,
    setCalendarUrl,
    autoRespond,
    handleAutoRespondToggle,
    interactionWebhook,
    setInteractionWebhook,
    dealWebhook,
    setDealWebhook,
    isSaveDisabled,
    isLoading,
    saveSettings,
    errors,
    showSuccessAlert,
    clearFieldAndSave,
    notifiedToEmail,
    setNotifiedToEmail,
    emailError,
    handleEmailChange,
    customFields,
    updateCustomField,
  } = usePlatformSettings();

  return (
    <div className="fullScreen bg-white h-full">
      {isLoading && <Loader />}

      <div className="container !h-full">
        <div className="savedFiltersScreen h-full pb-[75px] gap-3 mt-[20px] block">
          <div className="sfTitles flex flex-col items-start justify-between">
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-2">
                <Settings className="w-8 h-8 text-[var(--primary-700)]" />
                <span className="display-sm semibold text-gray-900">
                  {platformSettingsCopy.titlePlatformSettings}
                </span>
              </div>
              <span className="text-md regular text-gray-600 pl-10">
                {platformSettingsCopy.subHeadingPlatformSettings}
              </span>
            </div>
          </div>
          {showSuccessAlert && (
            <Alert
              variant="success"
              className="mb-4"
              title="Platform settings saved"
            />
          )}

          <ScrollArea className="h-[calc(100vh-220px)]">
            <div className="flex flex-col gap-4">
              <Card className="flex flex-col w-full">
                <CardHeader>
                  <CardTitle className="text-left">
                    {platformSettingsCopy.titleCampaignRepliesTo}
                  </CardTitle>
                  <span className="text-sm font-normal text-left">
                    {platformSettingsCopy.subTextCampaignRepliesTo}
                  </span>
                </CardHeader>

                <CardContent className="space-y-4 flex flex-col w-full">
                  <Label
                    htmlFor="notified-email"
                    className="flex items-center gap-2 text-slate-700"
                  >
                    {platformSettingsCopy.labelSendNotificationTo}
                  </Label>

                  <div className="flex items-center gap-2">
                    <Input
                      id="notified-email"
                      placeholder="example@bizcrunch.co"
                      value={notifiedToEmail}
                      onChange={(e) => {
                        setNotifiedToEmail(e.target.value);
                        handleEmailChange(e.target.value);
                      }}
                    />
                  </div>

                  {emailError && (
                    <span className="text-sm text-red-600 text-left">
                      {emailError}
                    </span>
                  )}
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader>
                  <CardTitle className="text-left">
                    {platformSettingsCopy.cardTitleCalendarLink}
                  </CardTitle>
                  <span className="text-sm font-normal text-left">
                    {platformSettingsCopy.subTextCalendarLink}
                  </span>
                </CardHeader>

                <CardContent className="space-y-4">
                  <Label
                    htmlFor="calendar-url"
                    className="flex items-center gap-2 text-slate-700"
                  >
                    {platformSettingsCopy.labelBookingCalendarUrl}
                  </Label>

                  <div className="flex items-center gap-2">
                    <Input
                      id="calendar-url"
                      placeholder="https://calendly.com/your-link"
                      value={calendarUrl}
                      onChange={(e) => setCalendarUrl(e.target.value)}
                    />
                    <button
                      className="hover:text-[var(--primary-700)]"
                      onClick={() => clearFieldAndSave("calendlyLink")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {errors.calendlyLink && (
                    <span className="flex text-sm text-red-600 text-left">
                      {errors.calendlyLink}
                    </span>
                  )}

                  <div className="flex items-center justify-between rounded-lg border p-4 bg-gray-50">
                    <div className="flex flex-col">
                      <span className="text-md font-semibold text-left">
                        {platformSettingsCopy.labelToggleCalendarLink}
                      </span>
                      <span className="text-sm font-normal text-muted-foreground">
                        {platformSettingsCopy.toggleSubText}
                      </span>
                    </div>
                    <ToolTipWrapper
                      content={`${
                        calendarUrl.trim() === ""
                          ? platformSettingsCopy.warningCalendarUrlNull
                          : ""
                      }`}
                    >
                      <Switch
                        checked={autoRespond}
                        onCheckedChange={handleAutoRespondToggle}
                        disabled={calendarUrl.trim() === ""}
                        className="disabled:opacity-100"
                      />
                    </ToolTipWrapper>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-left">
                    {platformSettingsCopy.cardTitleTriggeredWebhook}
                  </CardTitle>
                  <span className="text-sm font-normal text-left">
                    {platformSettingsCopy.subTextTriggeredWebhook}
                  </span>
                </CardHeader>

                <CardContent className="space-y-6 flex flex-row w-full gap-3">
                  <div className="w-[70%] flex flex-col gap-7">
                    <div className="flex flex-col gap-2 w-full">
                      <Label
                        htmlFor="interaction-webhook"
                        className="text-slate-700 text-left"
                      >
                        {platformSettingsCopy.labelOnInteractionWebhook}
                      </Label>

                      <div className="flex items-center gap-2">
                        <Input
                          id="interaction-webhook"
                          placeholder="https://api.example.com/webhook/interaction"
                          value={interactionWebhook}
                          onChange={(e) =>
                            setInteractionWebhook(e.target.value)
                          }
                        />
                        <button
                          className="hover:text-[var(--primary-700)]"
                          onClick={() =>
                            clearFieldAndSave("interactionWebhookUrl")
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {errors.interactionWebhookUrl && (
                        <span className="text-sm text-red-600 text-left">
                          {errors.interactionWebhookUrl}
                        </span>
                      )}

                      <span className="text-sm font-normal text-muted-foreground text-left">
                        {platformSettingsCopy.subTextOnInteractionWebhook}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2 w-full">
                      <Label
                        htmlFor="deal-creation-webhook"
                        className="text-slate-700 text-left"
                      >
                        {platformSettingsCopy.labelOnDealCreationWebhook}
                      </Label>

                      <div className="flex items-center gap-2">
                        <Input
                          id="deal-creation-webhook"
                          placeholder="https://api.example.com/webhook/deal"
                          value={dealWebhook}
                          onChange={(e) => setDealWebhook(e.target.value)}
                        />
                        <button
                          className="hover:text-[var(--primary-700)]"
                          onClick={() => clearFieldAndSave("dealWebhookUrl")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {errors.dealWebhookUrl && (
                        <span className="text-sm text-red-600 text-left">
                          {errors.dealWebhookUrl}
                        </span>
                      )}

                      <span className="text-sm font-normal text-muted-foreground text-left">
                        {platformSettingsCopy.subTextOnDealCreationWebhook}
                      </span>
                    </div>
                  </div>

                  <div className="w-[30%] flex flex-col text-left bg-gray-100 p-4 rounded-lg">
                    <div className="text-sm text-gray-700 font-semibold mb-1">
                      {platformSettingsCopy.titleExampleJsonPayload}
                    </div>
                    <pre className="text-sm text-gray-700 overflow-x-auto">
                      {`{
  "event_type": "interaction",
  "timestamp": "2024-01-08T14:30:00Z",
  "company": {
    "id": "01234567",
    "PSC_email": "user@example.com",
    "PSC_name": "John Doe",
    "name": "Company Name Ltd",
    "address": {
      "address_line1": "Unit 1",
      "address_line2": "Work Street",
      "city": "City",
      "postcode": "PostCode",
      "country": "United Kingdom"
    },
    "website": "www.example.com"
  }
}`}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              {/* <Card>
                <CardHeader>
                  <CardTitle className="text-left">
                    {platformSettingsCopy.titleCustomFields}
                  </CardTitle>
                  <span className="text-sm font-normal text-left">
                    {platformSettingsCopy.subTextCustomFields}
                  </span>
                </CardHeader>
                <CardContent className="space-y-4">
                  {customFields.map((field, index) => (
                    <div key={index} className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-2">
                        <Label className="text-left">
                          Field {index + 1} Name
                        </Label>
                        <Input
                          placeholder="Field name (max 50 chars)"
                          value={field.name}
                          onChange={(e) =>
                            updateCustomField(index, "name", e.target.value)
                          }
                          maxLength={50}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                          <Label className="text-left">
                            Field {index + 1} Value
                          </Label>
                          <div className="flex flex-row gap-2">
                            <Input
                              placeholder="Field value (max 50 chars)"
                              value={field.value}
                              onChange={(e) =>
                                updateCustomField(
                                  index,
                                  "value",
                                  e.target.value
                                )
                              }
                              maxLength={50}
                            />
                            <button
                              className="hover:text-[var(--primary-700)]"
                              onClick={() =>
                                clearFieldAndSave(
                                  index === 0 ? "customField1" : "customField2"
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card> */}
            </div>
          </ScrollArea>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end">
            <Button
              variant="primary"
              disabled={isSaveDisabled}
              className="mr-12"
              onClick={saveSettings}
            >
              {platformSettingsCopy.btnSaveSettings}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
